// Voice / AI-slop pattern scanner (PRD §4c, §11.5).
//
// **Output discipline**: findings are framed as *observed frequencies* the
// editor may want to verify. Never as AI-detection probabilities. PRD §5
// explicitly bans probability-based scoring (harmful to ESL writers).

import type { ManuscriptDocument } from '../pdf/types';
import { sectionAt } from '../pdf/sections';
import catalog from '../../../data/voice-patterns/lexical-tokens-v1.json';
import { stableFindingId } from './finding-id';
import type { PatternFinding } from './types';

interface TokenEntry {
  token: string;
  weight: number;
  context: string;
  exclude_contexts?: string[];
}

interface SyntacticEntry {
  id: string;
  description: string;
  pattern: string;
  flags?: string;
  weight: number;
}

interface Catalog {
  version: string;
  tokens: TokenEntry[];
  syntactic_patterns: SyntacticEntry[];
}

const CAT = catalog as unknown as Catalog;

/**
 * Surface threshold: occurrences per 1,000 words. Anything above this for a
 * weighted token becomes an observation. Tuned to keep noise low on normal
 * manuscripts.
 */
const PER_KW_THRESHOLD = 0.4;

function countOccurrences(haystackLower: string, needle: string): number[] {
  const n = needle.toLowerCase();
  if (!n) return [];
  const offsets: number[] = [];
  let idx = haystackLower.indexOf(n);
  while (idx >= 0) {
    offsets.push(idx);
    idx = haystackLower.indexOf(n, idx + n.length);
  }
  return offsets;
}

function isWordBoundary(text: string, idx: number, len: number): boolean {
  const before = idx === 0 ? '' : text[idx - 1];
  const after = idx + len >= text.length ? '' : text[idx + len];
  const isWord = (c: string) => /[a-z0-9_-]/i.test(c);
  return !isWord(before) && !isWord(after);
}

export function scanVoice(doc: ManuscriptDocument): PatternFinding[] {
  const findings: PatternFinding[] = [];
  const text = doc.rawText;
  const lower = text.toLowerCase();
  const wordCount = Math.max(1, doc.wordCount);

  for (const entry of CAT.tokens) {
    const offsets = countOccurrences(lower, entry.token).filter((idx) =>
      isWordBoundary(lower, idx, entry.token.length),
    );
    if (offsets.length === 0) continue;

    // Filter out excluded contexts (token appearing as part of another word/phrase).
    const filteredOffsets = entry.exclude_contexts
      ? offsets.filter((idx) => {
          const window = lower.slice(Math.max(0, idx - 40), idx + entry.token.length + 40);
          return !entry.exclude_contexts!.some((ex) => window.includes(ex.toLowerCase()));
        })
      : offsets;

    const perKw = (filteredOffsets.length * 1000) / wordCount;
    if (perKw < PER_KW_THRESHOLD) continue;
    const weightedRate = perKw * entry.weight;
    const severity = weightedRate > 1.5 ? 'amber' : 'info';

    const firstOffset = filteredOffsets[0];
    findings.push({
      id: stableFindingId('voice', `token:${entry.token}`, 0),
      category: 'voice',
      severity,
      checklistItemId: null,
      findingText: `Observed "${entry.token}" ${filteredOffsets.length} time${filteredOffsets.length === 1 ? '' : 's'} (${perKw.toFixed(1)} per 1,000 words). Voice catalog flag — verify editorially.`,
      evidencePassage: contextAround(text, firstOffset, entry.token.length),
      evidenceOffset: firstOffset,
      section: sectionAt(doc.sections, firstOffset),
      layer: 'pattern',
    });
  }

  for (const syn of CAT.syntactic_patterns) {
    let regex: RegExp;
    try {
      regex = new RegExp(syn.pattern, syn.flags ?? 'g');
    } catch {
      continue;
    }
    regex.lastIndex = 0;
    const offsets: number[] = [];
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
      offsets.push(m.index);
      if (offsets.length >= 10) break;
      if (m[0].length === 0) regex.lastIndex++;
    }
    if (offsets.length === 0) continue;
    const perKw = (offsets.length * 1000) / wordCount;
    const severity = perKw * syn.weight > 1.0 ? 'amber' : 'info';
    findings.push({
      id: stableFindingId('voice', `syntactic:${syn.id}`, 0),
      category: 'voice',
      severity,
      checklistItemId: null,
      findingText: `Syntactic pattern "${syn.id}" observed ${offsets.length} time${offsets.length === 1 ? '' : 's'}: ${syn.description}.`,
      evidencePassage: contextAround(text, offsets[0], 40),
      evidenceOffset: offsets[0],
      section: sectionAt(doc.sections, offsets[0]),
      layer: 'pattern',
    });
  }

  return findings;
}

function contextAround(text: string, offset: number, len: number): string {
  const before = Math.max(0, offset - 30);
  const after = Math.min(text.length, offset + len + 60);
  return text.slice(before, after).replace(/\s+/g, ' ').trim().slice(0, 200);
}
